import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, MessageCircle, MapPin, Phone, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SITE, waUrl } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const docTypes = [
  "Certidão (nascimento / casamento / óbito)",
  "Diploma ou histórico acadêmico",
  "Contrato",
  "Procuração",
  "Antecedentes criminais",
  "Documento empresarial",
  "Interpretação / evento",
  "Outro",
];

const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    document_type: docTypes[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.whatsapp) {
      toast.error("Preencha nome, e-mail e WhatsApp.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Mensagem enviada. Redirecionando para o WhatsApp…");
      const text = `Olá ${SITE.professionalName.split(" ")[0]}, sou ${form.name}.\n\nE-mail: ${form.email}\nWhatsApp: ${form.whatsapp}\nTipo de documento: ${form.document_type}\n\n${form.message || "Gostaria de solicitar um orçamento."}`;
      setTimeout(() => {
        window.open(waUrl(text), "_blank", "noopener");
      }, 600);
      setForm({ name: "", email: "", whatsapp: "", document_type: docTypes[0], message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível enviar. Tente novamente ou use o WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contato"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#0A1128] text-white overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(10,17,40,0.92) 0%, rgba(10,17,40,0.98) 100%), url(${SITE.media.officeAbstract})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5"
        >
          <p className="overline mb-5"><span className="gold-rule mr-3 align-middle" />Contato</p>
          <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight">
            Fale diretamente com <br />
            <span className="italic text-[#C5A059]">o tradutor</span>.
          </h2>
          <p className="mt-6 text-white/70 text-base lg:text-lg leading-relaxed max-w-md">
            Resposta em até 1 hora útil. Envie seus dados e o tipo de documento —
            seu pedido será redirecionado para o WhatsApp do profissional.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex items-start gap-4" data-testid="contact-info-phone">
              <Phone size={18} className="text-[#C5A059] mt-1" strokeWidth={1.5} />
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-white/50">WhatsApp</p>
                <a
                  href={waUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base text-white hover:text-[#C5A059] transition-colors"
                >
                  {SITE.whatsappDisplay}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4" data-testid="contact-info-email">
              <Mail size={18} className="text-[#C5A059] mt-1" strokeWidth={1.5} />
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-white/50">E-mail</p>
                <a
                  href={`mailto:${SITE.email}`}
                  className="text-base text-white hover:text-[#C5A059] transition-colors break-all"
                >
                  {SITE.email}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4" data-testid="contact-info-location">
              <MapPin size={18} className="text-[#C5A059] mt-1" strokeWidth={1.5} />
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase text-white/50">Localização</p>
                <p className="text-base text-white">{SITE.city}</p>
              </div>
            </li>
          </ul>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1 }}
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-white text-[#0A1128] p-8 md:p-12 rounded-sm"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#475569] font-semibold">Nome*</label>
              <input
                type="text"
                value={form.name}
                onChange={update("name")}
                required
                data-testid="form-name"
                className="mt-2 bg-transparent border-b border-[#E2E8F0] focus:border-[#C5A059] focus:outline-none py-3 w-full"
                placeholder="Seu nome completo"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#475569] font-semibold">E-mail*</label>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                data-testid="form-email"
                className="mt-2 bg-transparent border-b border-[#E2E8F0] focus:border-[#C5A059] focus:outline-none py-3 w-full"
                placeholder="voce@exemplo.com"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#475569] font-semibold">WhatsApp*</label>
              <input
                type="tel"
                value={form.whatsapp}
                onChange={update("whatsapp")}
                required
                data-testid="form-whatsapp"
                className="mt-2 bg-transparent border-b border-[#E2E8F0] focus:border-[#C5A059] focus:outline-none py-3 w-full"
                placeholder="+55 92 99999-0000"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#475569] font-semibold">Tipo de documento</label>
              <select
                value={form.document_type}
                onChange={update("document_type")}
                data-testid="form-document-type"
                className="mt-2 bg-transparent border-b border-[#E2E8F0] focus:border-[#C5A059] focus:outline-none py-3 w-full text-[#0A1128]"
              >
                {docTypes.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[11px] tracking-[0.2em] uppercase text-[#475569] font-semibold">Mensagem</label>
              <textarea
                value={form.message}
                onChange={update("message")}
                rows={4}
                data-testid="form-message"
                className="mt-2 bg-transparent border-b border-[#E2E8F0] focus:border-[#C5A059] focus:outline-none py-3 w-full resize-none"
                placeholder="Descreva brevemente sua necessidade (idioma, prazo, etc.). Documentos devem ser enviados em PDF."
              />
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              type="submit"
              disabled={loading}
              data-testid="form-submit-btn"
              className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1DA851] text-white px-7 py-4 rounded-sm transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            >
              <MessageCircle size={18} />
              <span className="font-medium tracking-wide">
                {loading ? "Enviando…" : "Enviar e Falar no WhatsApp"}
              </span>
            </button>
            <p className="text-xs text-[#475569]">
              Envie seus documentos em <span className="text-[#0A1128] font-medium">PDF</span> · Seus dados são tratados com sigilo absoluto.
            </p>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default ContactForm;
